import { z } from "zod";
import { timestampSchema } from "./timestamp";

/**
 * Cel — trzy rodzaje rozróżnione przez `kind`:
 *
 * - `individual_money` — cel oszczędzania dziecka (lub parenta dla siebie).
 *   Mierzony w `targetMinor` (PLN groszach). Postęp = `members.balanceMinor`
 *   właściciela. Dziecko może swój CRUD, parent zawsze.
 *
 * - `family_count` — wspólny cel rodziny mierzony w liczbie zadań pomocy
 *   rodzinie (kind="family") approved w HH od `createdAt` celu.
 *   `userId` puste, tylko parent CRUD. Jednostka: "zadań pomocy" 💛.
 *
 * - `family_offline_count` — wspólny cel rodziny mierzony w liczbie
 *   zadań offline (offline=true) approved w HH od `createdAt` celu.
 *   `userId` puste, tylko parent CRUD. Jednostka: "zadań offline" 🌳.
 *
 * Backward-compat: legacy dokumenty bez `kind` ale z `userId`+`targetMinor`
 * mappujemy w preprocess na `individual_money`. Stare household goals
 * (scope="household") nie pasują do żadnego kind → safeParse drop, znikają
 * z UI. Bez skryptu migracyjnego (jeden user, jedno środowisko).
 *
 * `title` jednocześnie pełni rolę nagrody dla family goals — np. "weekend
 * w górach". Aplikacja **nie księguje** przyznania nagrody, zgodnie z
 * *Sianko jest księgą, nie portfelem*.
 */
export const goalKindSchema = z.enum([
  "individual_money",
  "family_count",
  "family_offline_count",
]);
export type GoalKind = z.infer<typeof goalKindSchema>;

const goalCommon = {
  id: z.string(),
  title: z.string().min(1).max(60),
  emoji: z.string().max(8).optional(),
  createdAt: timestampSchema,
  createdBy: z.string(),
  completedAt: timestampSchema.optional(),
};

const individualMoneyGoalSchema = z.object({
  ...goalCommon,
  kind: z.literal("individual_money"),
  userId: z.string(),
  targetMinor: z.number().int().min(100).max(1_000_000_00),
  // Zasada 50/50 (blog: oszczedzanie-zasada-50-50): dziecko zbiera swoją
  // część pełnej ceny, dorosły dokłada resztę przy zakupie — poza aplikacją,
  // zgodnie z „księga, nie portfel". `targetMinor` pozostaje CZĘŚCIĄ DZIECKA
  // (= round(fullPriceMinor × childSharePct / 100)), więc progres, auto-mark
  // i recap działają bez zmian. Pola ustawia wyłącznie parent (rules);
  // wszystkie trzy występują razem albo wcale.
  fullPriceMinor: z.number().int().min(200).max(2_000_000_00).optional(),
  childSharePct: z.number().int().min(10).max(90).optional(),
  matchedBy: z.string().optional(),
});

const familyCountGoalSchema = z.object({
  ...goalCommon,
  kind: z.literal("family_count"),
  targetCount: z.number().int().min(1).max(1000),
});

const familyOfflineCountGoalSchema = z.object({
  ...goalCommon,
  kind: z.literal("family_offline_count"),
  targetCount: z.number().int().min(1).max(1000),
});

/**
 * Discriminated union po `kind`. Preprocess fallbackuje legacy dokumenty
 * bez `kind` (z `userId`+`targetMinor`) na `individual_money`.
 */
export const goalSchema = z.preprocess((data) => {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    if (
      typeof obj.kind === "undefined" &&
      typeof obj.userId === "string" &&
      typeof obj.targetMinor === "number"
    ) {
      return { ...obj, kind: "individual_money" };
    }
  }
  return data;
}, z.discriminatedUnion("kind", [
  individualMoneyGoalSchema,
  familyCountGoalSchema,
  familyOfflineCountGoalSchema,
]));
export type Goal = z.infer<typeof goalSchema>;

export type IndividualMoneyGoal = z.infer<typeof individualMoneyGoalSchema>;
export type FamilyCountGoal = z.infer<typeof familyCountGoalSchema>;
export type FamilyOfflineCountGoal = z.infer<typeof familyOfflineCountGoalSchema>;

/** Type guard — czy goal jest indywidualny pieniężny (legacy `Goal` typ). */
export function isIndividualMoneyGoal(goal: Goal): goal is IndividualMoneyGoal {
  return goal.kind === "individual_money";
}

/** Type guard — czy goal jest rodzinny (count lub offline_count). */
export function isFamilyGoal(
  goal: Goal,
): goal is FamilyCountGoal | FamilyOfflineCountGoal {
  return goal.kind === "family_count" || goal.kind === "family_offline_count";
}

/** Input do createGoal — individual_money. */
export const individualMoneyGoalInputSchema = z
  .object({
    userId: z.string(),
    title: z.string().min(1).max(60),
    emoji: z.string().max(8).optional(),
    targetMinor: z.number().int().min(100).max(1_000_000_00),
    fullPriceMinor: z.number().int().min(200).max(2_000_000_00).optional(),
    childSharePct: z.number().int().min(10).max(90).optional(),
    matchedBy: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    const fields = [v.fullPriceMinor, v.childSharePct, v.matchedBy];
    const set = fields.filter((f) => f !== undefined).length;
    if (set !== 0 && set !== fields.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Zasada 50/50 wymaga kompletu: fullPriceMinor, childSharePct, matchedBy",
      });
      return;
    }
    if (
      v.fullPriceMinor !== undefined &&
      v.childSharePct !== undefined &&
      v.targetMinor !==
        Math.round((v.fullPriceMinor * v.childSharePct) / 100)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "targetMinor musi być częścią dziecka: round(fullPriceMinor × childSharePct / 100)",
      });
    }
  });
export type IndividualMoneyGoalInput = z.infer<
  typeof individualMoneyGoalInputSchema
>;

/** Input do createGoal — family_count lub family_offline_count. */
export const familyGoalInputSchema = z.object({
  kind: z.enum(["family_count", "family_offline_count"]),
  title: z.string().min(1).max(60),
  emoji: z.string().max(8).optional(),
  targetCount: z.number().int().min(1).max(1000),
});
export type FamilyGoalInput = z.infer<typeof familyGoalInputSchema>;
